// src/pages/admin/CompanySettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import HolidayManagementPage from '../../components/admin/holidays/HolidayManagementPage';
import AppVersionLabel from '../../components/common/AppVersionLabel';
import './CompanySettingsPage.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CompanySettingsPage = () => {
  const { t } = useTranslation('settings');

  // Get currentUser (Firebase Auth) and currentCompany (Firestore) from auth context
  const { currentUser, currentCompany, updateUserEmail, resetPassword } = useAuth();
  const companyId = currentUser?.companyId || currentCompany?.id;

  // State for company settings
  const [companyName, setCompanyName] = useState('');
  const [autoClockOutGlobal, setAutoClockOutGlobal] = useState(false);
  const [enforceBreakCompliance, setEnforceBreakCompliance] = useState(false);

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showHolidayManagement, setShowHolidayManagement] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [privacyPolicyLoading, setPrivacyPolicyLoading] = useState(true);

  // Account settings state (email + password)
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState('');

  // Modern Loading Component
  const ModernLoadingIndicator = ({ message }) => (
    <div className="modern-loading-container">
      <div className="modern-loading-content">
        <div className="loading-progress-bar">
          <div className="loading-progress-fill"></div>
        </div>
        <p className="loading-message">{message}</p>
      </div>
    </div>
  );

  useEffect(() => {
    if (companyId) {
      loadCompanySettings();
    }
  }, [companyId]);

  const loadCompanySettings = async () => {
    if (!companyId) {
      setErrorMessage(t('companySettings.validation.invalidCompanyConfig'));
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      // Load company settings
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        const data = companyDoc.data();

        // Use currentCompany name if available, otherwise use data from Firestore
        const loadedCompanyName = currentCompany?.name || data.name || '';
        setCompanyName(loadedCompanyName);

        setAutoClockOutGlobal(data.autoClockOutGlobal || false);

        if (data.breakSettings) {
          setEnforceBreakCompliance(data.breakSettings.enforceComplianceRules || false);
        }
      } else {
        setErrorMessage(t('companySettings.validation.companySettingsNotFound'));
      }
    } catch (error) {
      console.error('Firebase error:', error);
      setErrorMessage(t('companySettings.validation.unableToLoadSettings'));
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validation
    const trimmedCompanyName = companyName.trim();

    if (!trimmedCompanyName) {
      setErrorMessage(t('companySettings.validation.companyNameRequired'));
      setIsLoading(false);
      return;
    }

    if (trimmedCompanyName.length > 100) {
      setErrorMessage(t('companySettings.validation.companyNameTooLong'));
      setIsLoading(false);
      return;
    }

    try {
      const settingsData = {
        name: trimmedCompanyName,
        autoClockOutGlobal,
        breakSettings: {
          enforceComplianceRules: enforceBreakCompliance
        }
      };

      await setDoc(doc(db, 'companies', companyId), settingsData, { merge: true });

      setSuccessMessage(t('companySettings.validation.settingsSavedSuccessfully'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setErrorMessage(t('companySettings.validation.unableToSaveSettings'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Send a verification link to the new email address.
   * The email in Firebase Auth only changes once the admin clicks the
   * confirmation link sent to the new inbox - no password/re-auth needed
   * to trigger this request.
   */
  const handleUpdateEmail = async () => {
    setEmailError('');
    setEmailSuccess('');

    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail) {
      setEmailError(t('companySettings.validation.emailRequired'));
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError(t('companySettings.validation.invalidEmailFormat'));
      return;
    }

    if (currentUser?.email && trimmedEmail.toLowerCase() === currentUser.email.toLowerCase()) {
      setEmailError(t('companySettings.validation.sameAsCurrentEmail'));
      return;
    }

    setIsUpdatingEmail(true);

    try {
      await updateUserEmail(trimmedEmail);
      setEmailSuccess(t('companySettings.validation.emailVerificationSent', { email: trimmedEmail }));
      setNewEmail('');
    } catch (error) {
      console.error('Email update error:', error);

      if (error.code === 'auth/requires-recent-login') {
        setEmailError(t('companySettings.validation.emailUpdateRequiresRecentLogin'));
      } else if (error.code === 'auth/email-already-in-use') {
        setEmailError(t('companySettings.validation.emailAlreadyInUse'));
      } else if (error.code === 'auth/invalid-email') {
        setEmailError(t('companySettings.validation.invalidEmailFormat'));
      } else {
        setEmailError(t('companySettings.validation.unableToUpdateEmail'));
      }
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  /**
   * Send a password reset email to the currently signed-in admin.
   * Simplest possible flow - no re-authentication required.
   */
  const handleSendPasswordReset = async () => {
    setPasswordResetError('');
    setPasswordResetSuccess('');

    if (!currentUser?.email) {
      setPasswordResetError(t('companySettings.validation.unableToSendPasswordReset'));
      return;
    }

    setIsSendingPasswordReset(true);

    try {
      await resetPassword(currentUser.email);
      setPasswordResetSuccess(
        t('companySettings.validation.passwordResetSent', { email: currentUser.email })
      );
    } catch (error) {
      console.error('Password reset error:', error);
      setPasswordResetError(t('companySettings.validation.unableToSendPasswordReset'));
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  // Handle holiday management close
  const handleHolidayManagementClose = () => {
    setShowHolidayManagement(false);
  };

  // Get the display name for the company label
  const getCompanyDisplayName = () => {
    // First try currentCompany from auth context
    if (currentCompany?.name) {
      return currentCompany.name;
    }
    // Then try the loaded company name
    if (companyName) {
      return companyName;
    }
    // Default
    return t('companySettings.labels.notSet');
  };

  // If holiday management is open, render it as the main content
  if (showHolidayManagement) {
    if (!companyId) {
      return (
        <div className="company-settings-page">
          <div className="settings-content">
            <div className="error-message">
              {t('companySettings.validation.invalidCompanyConfig')}
            </div>
            <button
              className="save-button"
              onClick={handleHolidayManagementClose}
            >
              {t('companySettings.actions.close')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <HolidayManagementPage
        companyId={companyId}
        onClose={handleHolidayManagementClose}
      />
    );
  }

  return (
    <div className="company-settings-page">
      <div className="settings-header">
        <h1>{t('companySettings.title')}</h1>
      </div>

      <div className="settings-content">
        {/* Company Information Section */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.companyInformation.title')}</h2>
          <div className="settings-section-content">
            <div className="form-group">
              <label className="form-label">
                {t('companySettings.sections.companyInformation.companyLabel')} <strong>{getCompanyDisplayName()}</strong>
              </label>
              <input
                type="text"
                placeholder={t('companySettings.sections.companyInformation.companyNamePlaceholder')}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="form-input"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Account Settings Section (Email + Password) */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.accountSettings.title')}</h2>
          <div className="settings-section-content">
            {/* Email */}
            <div className="form-group">
              <label className="form-label">
                {t('companySettings.sections.accountSettings.emailLabel')}{' '}
                <strong>{currentUser?.email || t('companySettings.labels.notSet')}</strong>
              </label>
              <div className="input-with-button">
                <input
                  type="email"
                  placeholder={t('companySettings.sections.accountSettings.emailPlaceholder')}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="form-input"
                  disabled={isUpdatingEmail}
                />
                <button
                  type="button"
                  className="save-button account-action-button"
                  onClick={handleUpdateEmail}
                  disabled={isUpdatingEmail}
                >
                  {isUpdatingEmail
                    ? t('companySettings.sections.accountSettings.updatingEmail')
                    : t('companySettings.sections.accountSettings.updateEmail')}
                </button>
              </div>
              <p className="help-text">
                {t('companySettings.sections.accountSettings.emailHelpText')}
              </p>
              {emailError && <div className="error-message">{emailError}</div>}
              {emailSuccess && <div className="success-message">{emailSuccess}</div>}
            </div>

            <div className="divider" />

            {/* Password */}
            <div className="form-group">
              <label className="form-label">
                {t('companySettings.sections.accountSettings.passwordLabel')}
              </label>
              <p className="help-text">
                {t('companySettings.sections.accountSettings.passwordHelpText')}
              </p>
              <button
                type="button"
                className="save-button account-action-button"
                onClick={handleSendPasswordReset}
                disabled={isSendingPasswordReset}
              >
                {isSendingPasswordReset
                  ? t('companySettings.sections.accountSettings.sendingPasswordReset')
                  : t('companySettings.sections.accountSettings.sendPasswordReset')}
              </button>
              {passwordResetError && <div className="error-message">{passwordResetError}</div>}
              {passwordResetSuccess && <div className="success-message">{passwordResetSuccess}</div>}
            </div>
          </div>
        </div>

        {/* Break Compliance Section */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.breakSettings.title')}</h2>
          <div className="settings-section-content">
            <label className="toggle-setting">
              <input
                type="checkbox"
                checked={enforceBreakCompliance}
                onChange={(e) => setEnforceBreakCompliance(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('companySettings.sections.breakSettings.enforceBreakCompliance')}</span>
            </label>
            <p className="help-text">
              {t('companySettings.sections.breakSettings.helpText')}
            </p>

            {enforceBreakCompliance && (
              <div className="break-requirements">
                <h4>{t('companySettings.sections.breakSettings.legalRequirements')}</h4>
                <div className="requirement-row">
                  <span className="requirement-label">{t('companySettings.sections.breakSettings.sixHoursWorked')}</span>
                  <span className="requirement-value">{t('companySettings.sections.breakSettings.thirtyMinBreak')}</span>
                </div>
                <div className="requirement-row">
                  <span className="requirement-label">{t('companySettings.sections.breakSettings.nineHoursWorked')}</span>
                  <span className="requirement-value">{t('companySettings.sections.breakSettings.fortyFiveMinBreak')}</span>
                </div>
                <p className="note">
                  {t('companySettings.sections.breakSettings.automaticBreakNote')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Holiday Management Section */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.holidayManagement.title')}</h2>
          <div className="settings-section-content">
            <p className="section-description">
              {t('companySettings.sections.holidayManagement.description')}
            </p>
            <button
              className="link-button"
              onClick={() => setShowHolidayManagement(true)}
              disabled={isLoading || !companyId}
              title={!companyId ? t('companySettings.validation.invalidCompanyConfig') : ''}
            >
              <span>{t('companySettings.sections.holidayManagement.manageHolidays')}</span>
              <i className="icon-calendar" />
            </button>
            {!companyId && (
              <p className="error-text">
                {t('companySettings.validation.invalidCompanyConfig')}
              </p>
            )}
          </div>
        </div>

        {/* Legal Information Section */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.legalInformation.title')}</h2>
          <div className="settings-section-content">
            <button
              className="link-button"
              onClick={() => {
                setPrivacyPolicyLoading(true);
                setShowPrivacyPolicy(true);
              }}
              disabled={isLoading}
            >
              <span>{t('companySettings.sections.legalInformation.privacyPolicy')}</span>
              <i className="icon-shield" />
            </button>
          </div>
        </div>

        {/* App Info Section */}
        <div className="settings-section">
          <h2>{t('companySettings.sections.appInfo.title')}</h2>
          <div className="settings-section-content">
            <div className="app-info-row">
              <span className="app-info-label">
                {t('companySettings.sections.appInfo.versionLabel')}
              </span>
              <AppVersionLabel className="app-info-value" showLabel={false} />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <button
            className="save-button"
            onClick={saveSettings}
            disabled={isLoading}
          >
            {isLoading ? t('companySettings.actions.saving') : t('companySettings.actions.saveSettings')}
          </button>
        </div>

        {/* Messages */}
        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="modal-overlay" onClick={() => setShowPrivacyPolicy(false)}>
          <div className="modal-content privacy-policy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('companySettings.sections.legalInformation.privacyPolicy')}</h2>
              <button
                className="close-button"
                onClick={() => setShowPrivacyPolicy(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {privacyPolicyLoading && (
                <ModernLoadingIndicator message={t('companySettings.labels.loadingPrivacyPolicy')} />
              )}
              <iframe
                src="https://klokify.de/privacy"
                title={t('companySettings.sections.legalInformation.privacyPolicy')}
                className="privacy-policy-iframe"
                onLoad={() => setPrivacyPolicyLoading(false)}
                style={{ display: privacyPolicyLoading ? 'none' : 'block' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySettingsPage;